// Read-only, mobile-first group-stage view for the public share link. Shows one
// glowing card per pool with live standings and completed results. All figures
// are derived from the snapshot via the pure domain helpers — there are no
// editing affordances. Styling follows the "sleek dark + glow" direction.

import { useMemo, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import type { Cell, TournamentState } from '../../domain';
import {
  computeFootballStandings,
  computeStandings,
  summarizeFootballMatch,
  summarizeMatch,
} from '../../domain';
import { useTranslation } from '../../state/I18nContext';
import { ACCENT_GRADIENT, glowCard } from './publicStyles';

interface PublicGroupsProps {
  tournament: TournamentState;
}

export function PublicGroups({ tournament }: PublicGroupsProps) {
  return (
    <Stack spacing={2}>
      {tournament.groupOrder.map((poolId) => (
        <GroupCard key={poolId} tournament={tournament} poolId={poolId} />
      ))}
    </Stack>
  );
}

function GroupCard({ tournament, poolId }: { tournament: TournamentState; poolId: string }) {
  const { t } = useTranslation();
  const group = tournament.groups[poolId];

  const cells = useMemo<Cell[]>(
    () => (group ? group.cellIds.map((id) => tournament.cells[id]) : []),
    [tournament, group],
  );
  const nameById = useMemo(() => {
    const map: Record<string, string> = {};
    if (group) {
      for (const pid of group.playerIds) map[pid] = tournament.players[pid]?.name ?? pid;
    }
    return map;
  }, [tournament, group]);
  const isFootball = tournament.sportId === 'FOOTBALL';
  const standings = useMemo(
    () =>
      group
        ? isFootball
          ? computeFootballStandings(group.playerIds, cells)
          : computeStandings(group.playerIds, cells)
        : [],
    [group, cells, isFootball],
  );

  if (!group) return null;

  const completedCells = cells.filter((c) => c.status === 'COMPLETED');
  // Football scores are live from the moment they are recorded; surface them
  // alongside the completed results so spectators can follow along.
  const liveCells = isFootball
    ? cells.filter((c) => c.status === 'IN_PROGRESS')
    : [];
  const allDone = cells.length > 0 && completedCells.length === cells.length;

  return (
    <Paper elevation={0} sx={[{ p: 2.25 }, glowCard(allDone)]}>
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1.75 }}>
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 2,
            display: 'grid',
            placeItems: 'center',
            backgroundImage: ACCENT_GRADIENT,
            color: '#ffffff',
            fontWeight: 800,
            fontSize: 16,
            flexShrink: 0,
          }}
        >
          {group.label}
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1 }}>
          {t('groups.group', { label: group.label })}
        </Typography>
        {allDone ? (
          <Chip size="small" color="success" icon={<CheckCircleIcon />} label={t('groups.completed')} sx={{ fontWeight: 700 }} />
        ) : (
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            label={t('groups.played', { done: completedCells.length, total: cells.length })}
            sx={{ fontWeight: 700 }}
          />
        )}
      </Stack>

      {/* Standings — the top two (rank ≤ 2) qualify for the bracket. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '32px 1fr 38px 38px',
          rowGap: 0.25,
          columnGap: 1,
          alignItems: 'center',
        }}
      >
        <HeadLabel align="center">{t('table.rank')}</HeadLabel>
        <HeadLabel>{isFootball ? t('table.team') : t('table.player')}</HeadLabel>
        <HeadLabel align="right">{t('table.matchesPlayed')}</HeadLabel>
        <HeadLabel align="right">
          {isFootball ? t('table.points') : t('table.wins')}
        </HeadLabel>
        {standings.map((s) => {
          const qualifies = s.rank <= 2;
          return (
            <Box
              key={s.playerId}
              sx={{
                display: 'contents',
                '& > *': {
                  py: 0.6,
                  bgcolor: qualifies ? 'rgba(244,63,94,0.08)' : 'transparent',
                },
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center', borderTopLeftRadius: 8, borderBottomLeftRadius: 8 }}>
                {qualifies ? (
                  <Chip
                    size="small"
                    label={s.rank}
                    color={s.rank === 1 ? 'primary' : 'secondary'}
                    sx={{ height: 20, fontWeight: 700, '& .MuiChip-label': { px: 0.75 } }}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary" align="center">
                    {s.rank}
                  </Typography>
                )}
              </Box>
              <Typography variant="body2" noWrap sx={{ fontWeight: qualifies ? 700 : 400, minWidth: 0 }}>
                {nameById[s.playerId] ?? s.playerId}
              </Typography>
              <Num>{s.matchesPlayed}</Num>
              <Box sx={{ borderTopRightRadius: 8, borderBottomRightRadius: 8 }}>
                <Num emphasis={qualifies}>
                  {'points' in s ? s.points : s.matchesWon}
                </Num>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Results so far. */}
      <Typography
        variant="overline"
        sx={{ display: 'block', mt: 2, mb: 0.5, color: 'text.secondary', letterSpacing: 1 }}
      >
        {t('groups.results')}
      </Typography>
      {completedCells.length + liveCells.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('groups.noMatches')}
        </Typography>
      ) : (
        <Stack divider={<Box sx={{ height: '1px', bgcolor: 'rgba(255,255,255,0.05)' }} />}>
          {liveCells.map((cell) => (
            <ResultRow key={cell.id} cell={cell} nameById={nameById} football live />
          ))}
          {completedCells.map((cell) => (
            <ResultRow key={cell.id} cell={cell} nameById={nameById} football={isFootball} />
          ))}
        </Stack>
      )}
    </Paper>
  );
}

function ResultRow({
  cell,
  nameById,
  football = false,
  live = false,
}: {
  cell: Cell;
  nameById: Record<string, string>;
  football?: boolean;
  live?: boolean;
}) {
  const { t } = useTranslation();
  let scoreA: number;
  let scoreB: number;
  if (football) {
    const summary = summarizeFootballMatch(cell.games);
    scoreA = summary.goalsA;
    scoreB = summary.goalsB;
  } else {
    const summary = summarizeMatch(cell.games, cell.bestOf);
    scoreA = summary.gamesA;
    scoreB = summary.gamesB;
  }
  const aWins = !live && scoreA > scoreB;
  const bWins = !live && scoreB > scoreA;
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
        alignItems: 'center',
        columnGap: 1,
        py: 0.85,
      }}
    >
      <Typography
        variant="body2"
        noWrap
        sx={{ textAlign: 'right', fontWeight: aWins ? 700 : 400, color: aWins ? 'text.primary' : 'text.secondary' }}
      >
        {nameById[cell.playerA] ?? cell.playerA}
      </Typography>
      <Box
        sx={{
          px: 1,
          py: 0.15,
          borderRadius: 1.5,
          bgcolor: 'rgba(255,255,255,0.05)',
          fontWeight: 800,
          fontSize: 13,
          color: live ? 'warning.main' : 'primary.main',
          letterSpacing: 0.5,
        }}
      >
        {scoreA}–{scoreB}
        {live && (
          <Box component="span" sx={{ ml: 0.5, fontSize: 10, fontWeight: 700 }}>
            {t('groups.live')}
          </Box>
        )}
      </Box>
      <Typography
        variant="body2"
        noWrap
        sx={{ fontWeight: bWins ? 700 : 400, color: bWins ? 'text.primary' : 'text.secondary' }}
      >
        {nameById[cell.playerB] ?? cell.playerB}
      </Typography>
    </Box>
  );
}

function HeadLabel({ children, align = 'left' }: { children: ReactNode; align?: 'left' | 'center' | 'right' }) {
  return (
    <Typography variant="caption" color="text.secondary" align={align} sx={{ fontWeight: 700, opacity: 0.85 }}>
      {children}
    </Typography>
  );
}

function Num({ children, emphasis = false }: { children: ReactNode; emphasis?: boolean }) {
  return (
    <Typography
      variant="body2"
      align="right"
      sx={{ fontWeight: emphasis ? 800 : 500, color: emphasis ? 'primary.main' : 'text.primary', pr: 0.5 }}
    >
      {children}
    </Typography>
  );
}
